import { Upload } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useUploadDemandMutation } from '@/services/api/demandReviewService';

interface DemandUploaderProps {
  matterId: string;
  workspaceId: string;
}

export const DemandUploader = ({
  matterId,
  workspaceId,
}: DemandUploaderProps) => {
  const [uploadDemand, { isLoading }] = useUploadDemandMutation();
  const [error, setError] = useState<string | null>(null);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;

      const file = acceptedFiles[0];
      setError(null);

      try {
        await uploadDemand({ matterId, workspaceId, file }).unwrap();
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to upload demand',
        );
      }
    },
    [matterId, workspaceId, uploadDemand],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        ['.docx'],
    },
    maxFiles: 1,
    disabled: isLoading,
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Upload Demand Document</CardTitle>
      </CardHeader>
      <CardContent>
        <div
          {...getRootProps()}
          className={`
            flex cursor-pointer flex-col items-center justify-center
            rounded-lg border-2 border-dashed p-12
            transition-colors
            ${
              isDragActive
                ? 'border-primary bg-primary/5'
                : 'border-muted-foreground/25 hover:border-primary/50'
            }
            ${isLoading ? 'cursor-not-allowed opacity-50' : ''}
          `}
        >
          <input {...getInputProps()} />
          <Upload
            className={`mb-4 h-12 w-12 ${
              isDragActive ? 'text-primary' : 'text-muted-foreground'
            }`}
          />
          {isLoading ? (
            <p className="text-muted-foreground">Uploading...</p>
          ) : isDragActive ? (
            <p className="text-primary">Drop the file here</p>
          ) : (
            <>
              <p className="text-center text-muted-foreground">
                Drag and drop your demand document here, or click to browse
              </p>
              <p className="mt-2 text-center text-sm text-muted-foreground/75">
                Supported formats: PDF, DOCX
              </p>
            </>
          )}
        </div>
        {error && (
          <div className="mt-4 rounded-md bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
